import supabase from '../services/supabase';
import { sendNotification } from './notificationService';
import { createPlace } from './placeService';
import { compressImage } from '../utils/imageOptimizer';

/**
 * Fetch posts with pagination
 * @param {number} page - Page number, starting from 1
 * @param {number} pageSize - Number of posts per page
 * @param {string} userId - ID of the user to filter posts by
 * @returns {Promise<Array>} - Array of posts
 */
export const fetchPosts = async (page = 1, pageSize = 10, userId = null) => {
  try {
    console.log('fetchPosts called with:', { page, pageSize, userId });
    
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    // Optimized query with specific field selection
    let query = supabase
      .from('posts')
      .select(`
        id,
        title,
        content,
        user_id,
        created_at,
        post_stats(likes_count, comments_count)
      `)
      .order('created_at', { ascending: false })
      .range(from, to);

    // If a userId is provided, filter posts by users they follow
    if (userId) {
      console.log('Fetching followed users for userId:', userId);
      
      // 1. Get the list of user IDs the current user follows
      const { data: followedUsersData, error: followedUsersError } = await supabase
        .from('followers')
        .select('followed_id')
        .eq('follower_id', userId);

      if (followedUsersError) {
        console.error('Error fetching followed users:', followedUsersError);
        throw followedUsersError;
      }

      const followedUserIds = followedUsersData.map(f => f.followed_id);
      const usersToShowPostsFrom = [...followedUserIds, userId];
      
      console.log('Users to show posts from:', usersToShowPostsFrom);

      // 2. Filter the posts query to only include posts from these users
      query = query.in('user_id', usersToShowPostsFrom);
    }
      
    // Execute the final query
    console.log('Executing posts query...');
    const { data, error } = await query;

    if (error) {
      console.error('Error fetching posts:', error);
      throw error;
    }

    console.log(`Fetched ${data?.length || 0} posts from database`);

    // Get post IDs for parallel fetching
    const postIds = data?.map(post => post.id) || [];
    const userIds = data?.map(post => post.user_id) || [];
    
    console.log('Fetching data in parallel for:', { postIds, userIds });
    
    // Single optimized query to get all related data
    const { data: relatedData, error: relatedError } = await supabase
      .from('posts')
      .select(`
        id,
        user_profiles!posts_user_id_fkey(user_id, username, profilePicture),
        post_locations(post_id, name, lat, lng, display_order),
        post_media(post_id, id, url, display_order, caption)
      `)
      .in('id', postIds);

    if (relatedError) {
      console.error('Error fetching related data:', relatedError);
      throw relatedError;
    }

    // Fetch post_stats data separately
    const { data: statsData, error: statsError } = await supabase
      .from('post_stats')
      .select('post_id, likes_count, comments_count')
      .in('post_id', postIds);

    if (statsError) {
      console.error('Error fetching post stats:', statsError);
      // Continue without stats rather than failing
    }

    // Process the related data
    const userMap = {};
    const locationMap = {};
    const mediaMap = {};
    const statsMap = {};

    relatedData?.forEach(post => {
      // User profiles
      if (post.user_profiles) {
        userMap[post.user_profiles.user_id] = post.user_profiles;
      }

      // Post locations
      if (post.post_locations && post.post_locations.length > 0) {
        locationMap[post.id] = post.post_locations;
      }

      // Post media
      if (post.post_media && post.post_media.length > 0) {
        mediaMap[post.id] = post.post_media.sort((a, b) => a.display_order - b.display_order);
      }
    });

    // Process stats data
    statsData?.forEach(stat => {
      statsMap[stat.post_id] = stat;
    });

    // Process media URLs (simplified - no more URL correction needed)
    Object.keys(mediaMap).forEach(postId => {
      mediaMap[postId] = mediaMap[postId].map(media => ({
        ...media,
        url: media.url // Use URL directly - no processing needed
      }));
    });
    
    console.log('Media map after processing:', mediaMap);

    // Process posts to flatten the structure for backward compatibility
    const processedPosts = data?.map(post => {
      // Get place from locations (now stored directly in post_locations)
      const locations = locationMap[post.id] || [];
      const place = locations.length > 0 ? {
        id: locations[0].id,
        name: locations[0].name,
        lat: locations[0].lat,
        lng: locations[0].lng
      } : null;

      // Get images from media (if using new schema) or from image_url column
      const media = mediaMap[post.id] || [];
      const postImages = media.sort((a, b) => a.display_order - b.display_order);

      // Use media from the normalized schema
      let finalPostImages = postImages;
      let image_url = postImages.length > 0 ? postImages[0].url : null;

      const processedPost = {
        ...post,
        user: userMap[post.user_id] || null,
        place_id: place?.id || post.place_id || null,
        place: place,
        post_images: finalPostImages,
        image_url: image_url,
        likes_count: statsMap[post.id]?.likes_count || 0,
        comments_count: statsMap[post.id]?.comments_count || 0
      };

      // Debug logging for each post (only if there are issues)
      if (!processedPost.post_images || processedPost.post_images.length === 0) {
        console.log(`Processing post ${post.id}:`, {
          title: processedPost.title,
          hasPostImages: processedPost.post_images.length > 0,
          postImagesCount: processedPost.post_images.length,
          likesCount: processedPost.likes_count,
          commentsCount: processedPost.comments_count
        });
      }

      return processedPost;
    }) || [];

    console.log(`Processed ${processedPosts.length} posts successfully`);
    
    // Log post details for debugging
    processedPosts.forEach(post => {
      console.log(`Post ${post.id}:`, {
        title: post.title,
        user: post.user?.username,
        images: post.post_images?.length || 0,
        place: post.place?.name
      });
    });

    return processedPosts;
  } catch (error) {
    console.error('Error in fetchPosts:', error);
    throw error;
  }
};

// Alias for fetchPosts to fix naming discrepancy
export const getPosts = fetchPosts;

/**
 * Create a new post with location information
 * @param {Object} postData - Post data
 * @param {Object} locationData - Location data (optional)
 * @returns {Promise<Object>} - The newly created post
 */
export const createPost = async (postData, locationData = null) => {
  try {
    // Ensure user_id is set and user is authenticated
    if (!postData.user_id) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to create a post');
      }
      postData.user_id = user.id;
    }
    
    // Remove place_id from postData since we don't use it anymore
    const { place_id, ...postDataWithoutPlace } = postData;
    
    console.log("Creating post with data:", postDataWithoutPlace);
    
    const { data: post, error: postError } = await supabase
      .from('posts')
      .insert([postDataWithoutPlace])
      .select()
      .single();
      
    if (postError) {
      console.error("Error inserting post:", postError);
      console.error("Error details:", {
        message: postError.message,
        details: postError.details,
        hint: postError.hint,
        code: postError.code
      });
      throw postError;
    }
    
    console.log("Created post:", post);

    // If we have location data, store it directly in post_locations
    if (locationData && locationData.name && locationData.lat && locationData.lng) {
      const { error: locationError } = await supabase
        .from('post_locations')
        .insert([{
          post_id: post.id,
          name: locationData.name,
          lat: locationData.lat,
          lng: locationData.lng,
          display_order: 0
        }]);

      if (locationError) {
        console.error("Error creating location data:", locationError);
        // Don't throw here, the post was created successfully
      } else {
        console.log("Created location data for post:", post.id);
      }
    }

    // Initialize post stats
    console.log("Creating post_stats entry for post ID:", post.id);
    const { data: statsData, error: statsError } = await supabase
      .from('post_stats')
      .insert([{
        post_id: post.id,
        likes_count: 0,
        comments_count: 0
      }])
      .select();

    if (statsError) {
      console.error("Error creating post stats:", statsError);
      console.error("Stats error details:", {
        message: statsError.message,
        details: statsError.details,
        hint: statsError.hint,
        code: statsError.code
      });
      // Don't throw here, the post was created successfully
      // But log the error for debugging
    } else {
      console.log("Successfully created post_stats entry:", statsData);
    }
    
    return post;
  } catch (error) {
    console.error('Error creating post:', error);
    throw error;
  }
};

/**
 * Like a post
 * @param {string} postId - ID of the post to like
 * @returns {Promise<void>}
 */
export const likePost = async (postId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');
    
    // Check if user already liked this post
    const { data: existingLike, error: checkError } = await supabase
      .from('post_likes')
      .select('id')
      .match({ post_id: postId, user_id: user.id })
      .maybeSingle();
      
    if (checkError) throw checkError;
    
    if (existingLike) {
      throw new Error('User has already liked this post');
    }
    
    // Insert the like
    const { error } = await supabase
      .from('post_likes')
      .insert([{ post_id: postId, user_id: user.id }]);
      
    if (error) {
      // Handle unique constraint violation
      if (error.code === '23505') {
        throw new Error('User has already liked this post');
      }
      throw error;
    }
    
    // Update post_stats with new like count
    const { data: likeCount, error: countError } = await supabase
      .from('post_likes')
      .select('id', { count: 'exact' })
      .eq('post_id', postId);
    
    if (countError) throw countError;
    
    console.log(`Updating post_stats for post ${postId} with ${likeCount.length} likes`);
    
    // Update post_stats table
    const { data: updateData, error: updateError } = await supabase
      .from('post_stats')
      .update({ 
        likes_count: likeCount.length,
        updated_at: new Date().toISOString()
      })
      .eq('post_id', postId)
      .select();
    
    if (updateError) {
      console.error("Error updating post_stats:", updateError);
      throw updateError;
    }
    
    console.log("Successfully updated post_stats:", updateData);
    
    // Notify post owner (non-blocking)
    try {
      const { data: postRow } = await supabase
        .from('posts')
        .select('user_id')
        .eq('id', postId)
        .single();
      if (postRow?.user_id) {
        await sendNotification({
          recipientId: postRow.user_id,
          actorId: user.id,
          type: 'post_like',
          postId
        });
      }
    } catch (_) {}
  } catch (error) {
    console.error('Error liking post:', error);
    throw error;
  }
};



/**
 * Check if the current user has liked a post
 * @param {string} postId - ID of the post to check
 * @returns {Promise<boolean>} - True if the user has liked the post
 */
export const isPostLikedByUser = async (postId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return false;
    
    const { data, error } = await supabase
      .from('post_likes')
      .select('id')
      .match({ post_id: postId, user_id: user.id })
      .maybeSingle();
      
    if (error) throw error;
    
    return !!data;
  } catch (error) {
    console.error('Error checking if post is liked:', error);
    return false;
  }
};

/**
 * Unlike a post
 * @param {string} postId - ID of the post to unlike
 * @returns {Promise<void>}
 */
export const unlikePost = async (postId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');
    
    // Check if user has liked this post
    const { data: existingLike, error: checkError } = await supabase
      .from('post_likes')
      .select('id')
      .match({ post_id: postId, user_id: user.id })
      .maybeSingle();
      
    if (checkError) throw checkError;
    
    if (!existingLike) {
      throw new Error('User has not liked this post');
    }
    
    // Delete the like
    const { error } = await supabase
      .from('post_likes')
      .delete()
      .match({ post_id: postId, user_id: user.id });
      
    if (error) throw error;
    
    // Update post_stats with new like count
    const { data: likeCount, error: countError } = await supabase
      .from('post_likes')
      .select('id', { count: 'exact' })
      .eq('post_id', postId);
    
    if (countError) throw countError;
    
    console.log(`Updating post_stats for post ${postId} with ${likeCount.length} likes (after unlike)`);
    
    // Update post_stats table
    const { data: updateData, error: updateError } = await supabase
      .from('post_stats')
      .update({ 
        likes_count: likeCount.length,
        updated_at: new Date().toISOString()
      })
      .eq('post_id', postId)
      .select();
    
    if (updateError) {
      console.error("Error updating post_stats:", updateError);
      throw updateError;
    }
    
    console.log("Successfully updated post_stats:", updateData);
  } catch (error) {
    console.error('Error unliking post:', error);
    throw error;
  }
};

/**
 * Delete a post
 * @param {string} postId - ID of the post to delete
 * @returns {Promise<void>}
 */
export const deletePost = async (postId) => {
  try {
    const { error } = await supabase
      .from('posts')
      .delete()
      .eq('id', postId);
      
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
};

/**
 * Get comments for a post
 * @param {string} postId - ID of the post to get comments for
 * @returns {Promise<Array>} - Array of comments
 */
export const getComments = async (postId) => {
  try {
    const { data, error } = await supabase
      .from('post_comments')
      .select(`
        *,
        user_profiles!post_comments_user_id_fkey(
          user_id,
          username,
          profilePicture
        )
      `)
      .eq('post_id', postId)
      .order('created_at', { ascending: true });
      
    if (error) throw error;
    
    // Transform the data to match expected structure
    const transformedData = (data || []).map(comment => ({
      ...comment,
      user: {
        user_id: comment.user_id,
        username: comment.user_profiles?.username || comment.username,
        profilePicture: comment.user_profiles?.profilePicture || comment.profilePicture
      }
    }));
    
    return transformedData;
  } catch (error) {
    console.error('Error fetching comments:', error);
    throw error;
  }
};

/**
 * Get posts by location proximity
 * @param {number} lat - Latitude of center point
 * @param {number} lng - Longitude of center point
 * @param {number} radiusKm - Radius in kilometers
 * @param {number} limit - Maximum number of posts to return
 * @returns {Promise<Array>} - Array of posts
 */
export const getPostsByLocation = async (lat, lng, radiusKm = 10, limit = 20) => {
  try {
    // This uses the Supabase PostGIS extension for geo queries
    const { data, error } = await supabase.rpc('get_posts_by_location', {
      latitude: lat,
      longitude: lng,
      radius_km: radiusKm,
      post_limit: limit
    });
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error fetching posts by location:', error);
    throw error;
  }
};

/**
 * Fetch posts for a specific user's profile page with privacy checks
 * @param {string} profileUserId - ID of the user whose profile we're viewing
 * @param {string} currentUserId - ID of the current user (optional)
 * @returns {Promise<Array>} - Array of posts
 */
export const fetchUserProfilePosts = async (profileUserId, currentUserId = null) => {
  try {
    console.log('fetchUserProfilePosts called with:', { profileUserId, currentUserId });
    
    // Get the profile to check privacy
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('is_private')
      .eq('user_id', profileUserId)
      .single();

    if (profileError) {
      console.error('Error fetching profile:', profileError);
      throw profileError;
    }

    console.log('Profile privacy setting:', profile);

    // Check if current user can view posts
    let canViewPosts = false;

    if (currentUserId === profileUserId) {
      // User viewing their own profile
      canViewPosts = true;
      console.log('User viewing own profile - can view posts');
    } else if (!profile.is_private) {
      // Public profile - anyone can view
      canViewPosts = true;
      console.log('Public profile - can view posts');
    } else {
      // Private profile - check if following
      const { data: followData, error: followError } = await supabase
        .from('followers')
        .select('followed_id')
        .eq('follower_id', currentUserId)
        .eq('followed_id', profileUserId)
        .single();

      canViewPosts = !followError && followData;
      console.log('Private profile - following check:', { canViewPosts, followData, followError });
    }

    if (!canViewPosts) {
      console.log('Cannot view posts - returning empty array');
      return []; // Return empty array for private profiles
    }

    // Fetch posts for this user
    const { data, error } = await supabase
      .from('posts')
      .select('*')
      .eq('user_id', profileUserId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching posts:', error);
      throw error;
    }

    console.log(`Fetched ${data?.length || 0} posts for user ${profileUserId}`);

    // Get user profile
    const { data: userProfile, error: userError } = await supabase
      .from('user_profiles')
      .select('user_id, username, profilePicture')
      .eq('user_id', profileUserId)
      .single();

    if (userError) {
      console.error('Error fetching user profile:', userError);
    }

    // Get post locations
    const postIds = data?.map(post => post.id) || [];
    let postLocations = null;
    
    try {
      const { data: locationData, error: locationError } = await supabase
        .from('post_locations')
        .select('post_id, name, lat, lng')
        .in('post_id', postIds);

      if (locationError) {
        console.error('Error fetching post locations:', locationError);
      } else {
        postLocations = locationData;
      }
    } catch (error) {
      console.log('post_locations table might not exist, continuing without location data');
      postLocations = [];
    }

    // Get post media
    let postMedia = null;
    
    try {
      const { data: mediaData, error: mediaError } = await supabase
        .from('post_media')
        .select('post_id, id, url, display_order, caption')
        .in('post_id', postIds)
        .order('display_order');

      if (mediaError) {
        console.error('Error fetching post media:', mediaError);
      } else {
        postMedia = mediaData;
      }
    } catch (error) {
      console.log('post_media table might not exist, continuing without media data');
      postMedia = [];
    }

    // Create maps
    const locationMap = {};
    if (postLocations) {
      postLocations.forEach(location => {
        if (!locationMap[location.post_id]) {
          locationMap[location.post_id] = [];
        }
        locationMap[location.post_id].push(location);
      });
    }

         const mediaMap = {};
     
     // Function to correct URL case sensitivity (removed cache busters for better performance)
     const correctImageUrl = (url) => {
       if (!url) return null;
       
       // Fix the case sensitivity issue in the Supabase URL
       let correctedUrl = url;
       
       // Handle different case variations
       const incorrectPatterns = [
         /sttvwhschLpqwuLLowvb\.supabase\.co/g,
         /sttvwhschLpqwullowvb\.supabase\.co/g,
         /sttvwhschlpgwullowvb\.supabase\.co/g,
         /sttvwhschlpgwullowvb\.supabase\.co/g
       ];
       
       incorrectPatterns.forEach(pattern => {
         correctedUrl = correctedUrl.replace(pattern, 'sttvwhschlpgwullowvb.supabase.co');
       });
       
       // Debug logging for URL correction
       if (url !== correctedUrl) {
         console.log('URL corrected:', {
           original: url,
           corrected: correctedUrl
         });
       }
       
       return correctedUrl;
     };
     
     if (postMedia) {
       postMedia.forEach(media => {
         if (!mediaMap[media.post_id]) {
           mediaMap[media.post_id] = [];
         }
         
         // Apply URL correction
         const correctedUrl = correctImageUrl(media.url);
         const imageUrl = correctedUrl || null;
         
         mediaMap[media.post_id].push({
           ...media,
           url: imageUrl
         });
       });
     }

    // Process posts to flatten the structure for backward compatibility
    const processedPosts = data?.map(post => {
      // Get place from locations (now stored directly in post_locations)
      const locations = locationMap[post.id] || [];
      const place = locations.length > 0 ? {
        id: locations[0].id,
        name: locations[0].name,
        lat: locations[0].lat,
        lng: locations[0].lng
      } : null;

      // Get images from media
      const media = mediaMap[post.id] || [];
      const postImages = media.sort((a, b) => a.display_order - b.display_order);

      // Use media from the normalized schema
      let finalPostImages = postImages;
      let image_url = postImages.length > 0 ? postImages[0].url : null;

      const processedPost = {
        ...post,
        user: userProfile || null,
        place_id: place?.id || post.place_id || null,
        place: place,
        post_images: finalPostImages,
        image_url: image_url
      };

      return processedPost;
    }) || [];

    console.log(`Processed ${processedPosts.length} posts for user profile`);
    return processedPosts;
  } catch (error) {
    console.error('Error in fetchUserProfilePosts:', error);
    throw error;
  }
}; 

/**
 * Get a single post by ID
 * @param {string} postId - The ID of the post to fetch
 * @returns {Promise<Object>} - The post object
 */
export const getPostById = async (postId) => {
  try {
    console.log('getPostById called with:', postId);
    
    // Fetch the post with all related data
    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        user_profiles!posts_user_id_fkey(user_id, username, profilePicture),
        post_locations(post_id, name, lat, lng, display_order),
        post_media(post_id, id, url, display_order, caption)
      `)
      .eq('id', postId)
      .single();

    if (error) {
      console.error('Error fetching post:', error);
      throw error;
    }

    if (!data) {
      console.log('Post not found');
      return null;
    }

    // Process the post data similar to other functions
    const post = data;
    const userProfile = post.user_profiles;
    const postLocations = post.post_locations;
    const postMedia = post.post_media;

    // Get place from locations (now stored directly in post_locations)
    const locations = postLocations || [];
    const place = locations.length > 0 ? {
      id: locations[0].id,
      name: locations[0].name,
      lat: locations[0].lat,
      lng: locations[0].lng
    } : null;

    // Get images from media
    const media = postMedia || [];
    const postImages = media.sort((a, b) => a.display_order - b.display_order);

    // Use media from the normalized schema
    let finalPostImages = postImages;
    let image_url = postImages.length > 0 ? postImages[0].url : null;

    const processedPost = {
      ...post,
      user: userProfile || null,
      place_id: place?.id || post.place_id || null,
      place: place,
      post_images: finalPostImages,
      image_url: image_url,
      location: place ? {
        name: place.name,
        coordinates: place.coordinates
      } : null
    };

    console.log('Processed post:', processedPost);
    return processedPost;
  } catch (error) {
    console.error('Error in getPostById:', error);
    throw error;
  }
};

/**
 * Update an existing post
 * @param {string} postId - The ID of the post to update
 * @param {Object} postData - The updated post data
 * @param {Array} newImages - Array of new image files (optional)
 * @returns {Promise<Object>} - The updated post object
 */
export const updatePost = async (postId, postData, newImages = [], locationData = null) => {
  try {
    console.log('updatePost called with:', { postId, postData, newImagesCount: newImages.length, locationData });
    
    // Update the main post data (remove place_id and coordinates since we don't use them anymore)
    const { place_id, coordinates, ...postDataWithoutLocation } = postData;
    const { data: updatedPost, error: updateError } = await supabase
      .from('posts')
      .update({
        title: postData.title,
        content: postData.content,
        date: postData.date,
        country: postData.country,
        tags: postData.tags,
        updated_at: new Date().toISOString()
      })
      .eq('id', postId)
      .select()
      .single();

    if (updateError) {
      console.error('Error updating post:', updateError);
      throw updateError;
    }

    // Handle location data if provided
    if (locationData && locationData.name && locationData.lat && locationData.lng) {
      console.log('Updating location data for post:', postId);
      
      // First, delete existing location data
      const { error: deleteError } = await supabase
        .from('post_locations')
        .delete()
        .eq('post_id', postId);

      if (deleteError) {
        console.error('Error deleting existing location data:', deleteError);
      }

      // Insert new location data
      const { error: locationError } = await supabase
        .from('post_locations')
        .insert([{
          post_id: postId,
          name: locationData.name,
          lat: locationData.lat,
          lng: locationData.lng,
          display_order: 0
        }]);

      if (locationError) {
        console.error('Error updating location data:', locationError);
        // Don't throw here, the post was updated successfully
      } else {
        console.log('Successfully updated location data for post:', postId);
      }
    }

    // Handle new images if provided
    if (newImages.length > 0) {
      console.log('Processing new images for post update');
      
      // Upload new images
      const uploadedImages = [];
      for (let i = 0; i < newImages.length; i++) {
        const image = newImages[i];
        const fileName = `${postId}_${Date.now()}_${i}.jpg`;
        const filePath = `post-images/${fileName}`;
        
        // Compress image before upload
        const compressedImage = await compressImage(image, 0.8);
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('post-images')
          .upload(filePath, compressedImage, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
          throw uploadError;
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('post-images')
          .getPublicUrl(filePath);

        uploadedImages.push({
          post_id: postId,
          url: urlData.publicUrl,
          display_order: i,
          media_type: 'image'
        });
      }

      // Insert new images into post_media table
      if (uploadedImages.length > 0) {
        const { error: mediaError } = await supabase
          .from('post_media')
          .insert(uploadedImages);

        if (mediaError) {
          console.error('Error inserting new images:', mediaError);
          throw mediaError;
        }
      }
    }

    console.log('Post updated successfully:', updatedPost);
    return updatedPost;
  } catch (error) {
    console.error('Error in updatePost:', error);
    throw error;
  }
}; 
